import {
  CommitteeEmployee,
  InstitutionCommittee,
  CountyCommittee,
  RegionalCommittee,
  MinistryCommittee,
} from "../../../models/Committee";
import { addRowKeyIdToTableData } from "./table.util";

type CommitteeType =
  | InstitutionCommittee
  | CountyCommittee
  | RegionalCommittee
  | MinistryCommittee;

/**
 * Updates a specific employee in a committee's employee list by matching the rowKeyId
 *
 * @template T - Type extending CommitteeType (Institution, County, Regional or Ministry Committee)
 * @param {T} committee - The committee object containing the employee list to update
 * @param {CommitteeEmployee} updatedEmployee - The updated employee data to replace existing employee
 * @returns {T} A new committee object with the updated employee list
 *
 * @example
 * const updatedCommittee = updateCommitteeEmployee(committee, {
 *   rowKeyId: 1,
 *   name: "John Doe",
 *   role: 1
 * });
 */
export const updateCommitteeEmployee = <T extends CommitteeType>(
  committee: T,
  updatedEmployee: CommitteeEmployee
): T => {
  const copiedCommittee = { ...committee };

  copiedCommittee.employees = copiedCommittee.employees?.map((emp) => {
    if (emp.rowKeyId === updatedEmployee.rowKeyId) {
      return updatedEmployee;
    }
    return emp;
  });

  return copiedCommittee;
};

/**
 * Sets up committee employees with unique identifiers and updates both committee and table data states
 *
 * @template T - Type extending CommitteeType (Institution, County, Regional or Ministry Committee)
 * @param {T} committee - The committee object containing the employee list
 * @param {function} setCommittee - State setter function to update the committee data
 * @param {function} setTableData - State setter function to update the table display data
 *
 * @description
 * This function:
 * 1. Adds unique rowKeyId to each employee in the committee
 * 2. Updates the committee state with the modified employee list
 * 3. Updates the table data state with the same employee list
 *
 * @example
 * setupCommitteeEmployees(
 *   committee,
 *   setCommittee,
 *   setTableData
 * );
 */
export const setupCommitteeEmployees = <T extends CommitteeType>(
  committee: T,
  setCommittee: (committee: T) => void,
  setTableData: (employees: CommitteeEmployee[]) => void
) => {
  const employeesWithIds = addRowKeyIdToTableData(committee);

  setCommittee({ ...committee, employees: employeesWithIds });
  setTableData(employeesWithIds);
};
